
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import IconCamera from '@/components/svg/IconCamera.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import { CURRENT_CAMERA_KEY } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { promptVideoPermission, stopStreams } from '@/utils/helpers'
import mitt from '@/utils/mitt'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { createWorker, Worker } from 'tesseract.js'
import { mixins, Options } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import BulkScanSwitchButton from './common/BulkScanSwitchButton.vue'
import BarcodeReaderMixin from './mixins/BarcodeReaderMixin.vue'
import { isEmpty } from '@/utils/typeChecker'
import { TError } from '@/utils/types/commonTypes'
@Options({
  components: {
    IconCamera,
    IconArrowBack,
    IconSwitch,
    SelectionCameraPopup,
    BulkScanSwitchButton,
  },
  emits: ['update:modelValue', 'update:confirm', 'update:img'],
  name: 'OCRComponent',
})
export default class OCRComponent extends mixins(BarcodeReaderMixin) {
  @Prop({ type: String }) readonly fieldName!: string
  @Prop({ type: [String, Number] }) readonly initialValue?: string | number
  @Prop({ type: Boolean }) readonly isShowMultipleScanData?: boolean

  isRecognizing = false
  isRecognized = true
  loading = true
  video: HTMLVideoElement | null = null
  pictureWidth = 320
  pictureHeight = 320
  isCaptured = false
  croppedImg = {} as HTMLCanvasElement
  img = ''
  cropper = {} as Cropper
  worker = {} as Worker
  activeStreams = [] as MediaStream[]
  cameras = [] as MediaDeviceInfo[]
  selectedCamera = ''
  isOpenDialog = false

  get currentCameraId() {
    return localStorage.getItem(CURRENT_CAMERA_KEY) ?? ''
  }

  @Watch('selectedCamera')
  async setCameraId() {
    this.isOpenDialog = false
    this.vidOff()
    localStorage.setItem(CURRENT_CAMERA_KEY, this.selectedCamera)
    await promptVideoPermission()
    await this.setupVideo()
  }

  async initCameraSelection() {
    const devices = await navigator.mediaDevices.enumerateDevices()
    this.cameras = devices.filter((device) => device.kind === 'videoinput')
    if (this.currentCameraId) {
      const currentCamera = this.cameras.filter((device: MediaDeviceInfo) => device.deviceId === this.currentCameraId)
      if (currentCamera) {
        this.selectedCamera = this.currentCameraId
      }
    }
  }

  openDialog() {
    this.isOpenDialog = true
  }

  onEmitCapturedImage() {
    mitt.emit('update:imageOCR', { [this.fieldName]: this.img })
  }

  confirm() {
    this.vidOff()
    this.onEmitCapturedImage()
    this.$emit('update:confirm', this.valueCode.trim())
  }

  async reTakePicture() {
    if (this.valueCode === '') {
      await this.handleReTakePicture()
      return
    }
    this.$confirm(this.$t('confirm_reset_scanned_value'), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      cancelButtonText: this.$t('cancel'),
      type: 'warning',
    }).then(async () => {
      await this.handleReTakePicture()
    })
  }

  async handleReTakePicture() {
    this.isCaptured = false
    this.$emit('update:img', false)
    this.valueCode = ''
    this.img = ''
    this.cropper.destroy()
    const container = Array.from(document.getElementsByClassName('image-container') as HTMLCollectionOf<HTMLElement>)
    container[0].style.display = 'none'
    await this.setupVideo()
  }

  async getCropData() {
    try {
      this.croppedImg = this.cropper.getCroppedCanvas({
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      })
      if (!this.croppedImg) return
      if (this.isRecognized) return

      await this.onRecognize()
    } catch (error) {
      console.error(error)
    }
  }

  async onRecognize() {
    this.isRecognizing = true
    try {
      const {
        data: { text },
      } = await this.worker.recognize(this.croppedImg)
      if (this.showMultipleScan && this.isMultipleScan) {
        this.handleMultipleScan(text)
        return
      }
      this.valueCode = text
    } catch (e) {
      errorHandler(e as TError)
    } finally {
      this.isRecognizing = false
    }
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }

    const contentDialog = this.getContentDialog(plainText)
    this.isHandleMultipleScan = true

    this.$confirm(contentDialog, this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
      dangerouslyUseHTMLString: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  processValueCode(value: string) {
    let result = ''
    if (this.valueCode.trim() !== '') {
      result = `${this.valueCode.trim()} ${value}`
    } else {
      result = value
    }
    return result.trim()
  }

  async beforeCreate() {
    try {
      this.worker = await createWorker('eng')
    } catch (e) {
      errorHandler(e as TError)
    }
  }

  async beforeUnmount() {
    if (this.worker) {
      await this.worker.terminate()
    }
    if (!isEmpty(this.cropper)) {
      // eslint-disable-next-line no-unused-expressions
      this.cropper?.clear()
      // eslint-disable-next-line no-unused-expressions
      this.cropper?.reset()
      // eslint-disable-next-line no-unused-expressions
      this.cropper?.destroy()
    }
    if (this.img) {
      URL.revokeObjectURL(this.img)
    }

    this.vidOff()
  }

  async mounted() {
    // Set initial value if provided
    if (this.initialValue) {
      this.valueCode = this.initialValue.toString()
    }
    this.showMultipleScan = this.isShowMultipleScanData ?? false
    await this.initCameraSelection()
    await promptVideoPermission()
    await this.setupVideo()
  }

  get guideText() {
    return this.isCaptured
      ? isEmpty(this.valueCode)
        ? 'please_select_ocr_text'
        : 'please_confirm_ocr_text'
      : 'please_take_ocr_area'
  }

  startCrop() {
    const image = document.getElementById('imgTag') as HTMLImageElement
    if (!image) return
    this.cropper = new Cropper(image, {
      center: false,
      highlight: false,
      viewMode: 3,
      autoCrop: false,
      background: false,
      autoCropArea: 1,
      cropend: async () => {
        this.isRecognized = false
        await this.getCropData()
      },
    })
  }

  async vidOff() {
    stopStreams(this.activeStreams)
  }

  resetScannedValue(isShowConfirmation = false) {
    if (!isShowConfirmation) {
      this.valueCode = ''
      return
    }
    this.$confirm(this.$t('confirm_reset_scanned_value'), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      cancelButtonText: this.$t('cancel'),
      type: 'warning',
    }).then(() => {
      this.valueCode = ''
    })
  }

  snapshot() {
    this.isCaptured = true
    this.$emit('update:img', true)
    const canvas = document.querySelector('canvas')
    const container = Array.from(document.getElementsByClassName('image-container') as HTMLCollectionOf<HTMLElement>)
    if (!canvas) return
    canvas.width = this.pictureWidth
    canvas.height = this.pictureHeight
    const context = canvas?.getContext('2d')
    if (!context) return
    context.fillRect(0, 0, this.pictureWidth, this.pictureHeight)
    context.drawImage(this.video as CanvasImageSource, 0, 0, this.pictureWidth, this.pictureHeight)
    canvas.style.display = 'none'
    container[0].style.display = 'block'
    this.img = canvas.toDataURL()
    this.vidOff()
    setTimeout(() => {
      this.startCrop()
    }, 50)
  }

  async takeMoreSnapshot() {
    this.isCaptured = false
    this.$emit('update:img', false)
    this.img = ''
    this.cropper.destroy()
    const container = Array.from(document.getElementsByClassName('image-container') as HTMLCollectionOf<HTMLElement>)
    container[0].style.display = 'none'
    await this.setupVideo()
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 1000 },
            height: { ideal: 1000 },
            frameRate: {
              ideal: 30,
              max: 60,
            },
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }

  async setStream(stream: MediaStream) {
    this.activeStreams.push(stream)
    // Setup the video stream
    if (this.video) {
      this.video.srcObject = stream
      const streamSetting = stream.getVideoTracks()[0].getSettings()
      // actual width & height of the camera video
      this.pictureWidth = streamSetting.width || this.pictureWidth
      this.pictureHeight = streamSetting.height || this.pictureHeight
    }
  }
}
